import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import SessionManager from '../../components/ui/SessionManager';
import DocumentUpload from '../../components/ui/DocumentUpload';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { propertiesService } from '../../services/supabaseService';

const TenantOnboardingManagementScreen = () => {
  const [formData, setFormData] = useState({
    tenantName: '',
    tenantEmail: '',
    tenantPhone: '',
    propertyId: '',
    moveInDate: '',
    depositProtected: false,
    notes: ''
  });

  const [documents, setDocuments] = useState({
    idVerification: { status: 'missing', files: [] },
    leaseAgreement: { status: 'missing', files: [] },
    consentForms: { status: 'missing', files: [] }
  });

  const [activeUpload, setActiveUpload] = useState(null);
  const [properties, setProperties] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    propertiesService?.getAll()?.then(data => setProperties(data));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleDocumentUpload = (docType, files) => {
    setDocuments({
      ...documents,
      [docType]: { status: 'uploaded', files }
    });
    setActiveUpload(null);
    toast?.success('Document uploaded successfully');
  };

  const getStatusColor = (status) => {
    if (status === 'missing') return 'var(--color-error)';
    if (status === 'uploaded') return 'var(--color-warning)';
    if (status === 'verified') return 'var(--color-success)';
    return 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    if (status === 'missing') return 'Missing';
    if (status === 'uploaded') return 'Uploaded';
    if (status === 'verified') return 'Verified';
    return 'Unknown';
  };

  const toggleDocumentStatus = (docType) => {
    const currentStatus = documents?.[docType]?.status;
    let newStatus = currentStatus;
    if (currentStatus === 'uploaded') newStatus = 'verified';
    else if (currentStatus === 'verified') newStatus = 'uploaded';
    setDocuments({ ...documents, [docType]: { ...documents?.[docType], status: newStatus } });
  };

  const calculateProgress = () => {
    const requiredFields = ['tenantName', 'tenantEmail', 'tenantPhone', 'propertyId', 'moveInDate'];
    const filledFields = requiredFields?.filter(field => formData?.[field]);
    const requiredDocs = Object.values(documents)?.filter(doc => doc?.status !== 'missing');
    const fieldProgress = (filledFields?.length / requiredFields?.length) * 50;
    const docProgress = (requiredDocs?.length / 3) * 50;
    return Math.round(fieldProgress + docProgress);
  };

  const isFormValid = () => {
    const requiredFields = ['tenantName', 'tenantEmail', 'tenantPhone', 'propertyId', 'moveInDate'];
    const allFieldsFilled = requiredFields?.every(field => formData?.[field]);
    const allDocsUploaded = Object.values(documents)?.every(doc => doc?.status !== 'missing');
    return allFieldsFilled && allDocsUploaded;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast?.error('Please complete all required fields and upload all documents');
      return;
    }
    try {
      setSubmitting(true);
      await new Promise(resolve => setTimeout(resolve, 800));
      toast?.success('Tenant onboarding completed successfully!');
      setFormData({ tenantName: '', tenantEmail: '', tenantPhone: '', propertyId: '', moveInDate: '', depositProtected: false, notes: '' });
      setDocuments({ idVerification: { status: 'missing', files: [] }, leaseAgreement: { status: 'missing', files: [] }, consentForms: { status: 'missing', files: [] } });
    } catch (error) {
      toast?.error('Failed to complete onboarding. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const generateEmailReminder = () => {
    const missingDocs = Object.entries(documents)?.filter(([_, doc]) => doc?.status === 'missing')?.map(([key]) => key?.replace(/([A-Z])/g, ' $1')?.trim());
    if (missingDocs?.length === 0) { toast?.error('No missing documents to remind about'); return; }
    toast?.success('Email reminder template generated');
  };

  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      <SessionManager />
      <Header />
      <main className="pt-[108px] px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-foreground font-heading mb-2">Tenant Onboarding</h1>
            <p className="text-muted-foreground">Complete tenant details and upload required documents</p>
          </div>

          <div className="mb-6 p-4 rounded-lg bg-surface border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Onboarding Progress</span>
              <span className="text-sm font-semibold text-accent">{progress}%</span>
            </div>
            <div className="h-3 bg-input rounded-full overflow-hidden">
              <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <div className="card">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon name="User" size={20} color="var(--color-accent)" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground font-heading">Tenant Details</h2>
                </div>

                <div className="space-y-4">
                  <Input label="Tenant Name" type="text" placeholder="Enter full name" value={formData?.tenantName} onChange={(e) => handleInputChange('tenantName', e?.target?.value)} required />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Email Address" type="email" placeholder="tenant@example.com" value={formData?.tenantEmail} onChange={(e) => handleInputChange('tenantEmail', e?.target?.value)} required />
                    <Input label="Phone Number" type="tel" placeholder="07700 900123" value={formData?.tenantPhone} onChange={(e) => handleInputChange('tenantPhone', e?.target?.value)} required />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground font-caption mb-2 block">Property <span className="text-error">*</span></label>
                    <select
                      className="w-full h-10 px-3 py-2 rounded-md border border-input bg-background text-foreground transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
                      value={formData?.propertyId}
                      onChange={(e) => handleInputChange('propertyId', e?.target?.value)}
                    >
                      <option value="">Select property</option>
                      {properties?.map(prop => (
                        <option key={prop?.id} value={prop?.id}>
                          {prop?.propertyId} - {prop?.address1}, {prop?.postcode}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Move-in Date" type="date" value={formData?.moveInDate} onChange={(e) => handleInputChange('moveInDate', e?.target?.value)} required />
                    <div className="flex items-end">
                      <label className="flex items-center gap-3 h-10 cursor-pointer">
                        <input type="checkbox" checked={formData?.depositProtected} onChange={(e) => handleInputChange('depositProtected', e?.target?.checked)} className="h-5 w-5 rounded border border-input bg-background text-accent focus:ring-2 focus:ring-ring" />
                        <span className="text-sm font-medium text-foreground">Deposit Protected</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground font-caption mb-2 block">Notes</label>
                    <textarea className="w-full h-24 px-4 py-3 rounded-lg bg-input border border-border text-foreground transition-smooth focus:outline-none focus:ring-2 focus:ring-ring resize-none" placeholder="Additional notes or comments" value={formData?.notes} onChange={(e) => handleInputChange('notes', e?.target?.value)} />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Icon name="FileText" size={20} color="var(--color-warning)" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground font-heading">Required Documents</h2>
                </div>

                <div className="space-y-6">
                  {[{ key: 'idVerification', label: 'ID Verification', accept: '.pdf,.jpg,.jpeg,.png', uploadLabel: 'Upload ID Document' },
                    { key: 'leaseAgreement', label: 'Lease Agreement', accept: '.pdf', uploadLabel: 'Upload Lease Agreement (PDF only)' },
                    { key: 'consentForms', label: 'Consent Forms', accept: '.pdf,.jpg,.jpeg,.png,.docx', uploadLabel: 'Upload Consent Forms', multiple: true }]?.map(({ key, label, accept, uploadLabel, multiple }) => (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
                        <button
                          onClick={() => toggleDocumentStatus(key)}
                          className="px-3 py-1 rounded text-xs font-medium transition-smooth"
                          style={{ backgroundColor: `${getStatusColor(documents?.[key]?.status)}20`, color: getStatusColor(documents?.[key]?.status) }}
                          disabled={documents?.[key]?.status === 'missing'}
                        >
                          {getStatusLabel(documents?.[key]?.status)}
                        </button>
                      </div>
                      {activeUpload === key ? (
                        <DocumentUpload onUpload={(files) => handleDocumentUpload(key, files)} accept={accept} label={uploadLabel} multiple={multiple} />
                      ) : (
                        <Button variant="outline" fullWidth iconName="Upload" iconPosition="left" onClick={() => setActiveUpload(key)}>
                          {documents?.[key]?.files?.length > 0 ? 'Replace Document' : 'Upload Document'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="card sticky top-[124px]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground font-heading">Document Checklist</h2>
                </div>

                <div className="space-y-4">
                  {Object.entries(documents)?.map(([key, doc]) => (
                    <div key={key} className="p-4 rounded-lg bg-input border border-border">
                      <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${getStatusColor(doc?.status)}20` }}>
                          <Icon name={doc?.status === 'verified' ? 'Check' : doc?.status === 'uploaded' ? 'Clock' : 'X'} size={14} color={getStatusColor(doc?.status)} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground mb-1">
                            {key?.replace(/([A-Z])/g, ' $1')?.trim()?.charAt(0)?.toUpperCase() + key?.replace(/([A-Z])/g, ' $1')?.trim()?.slice(1)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Status: <span style={{ color: getStatusColor(doc?.status) }}>{getStatusLabel(doc?.status)}</span>
                          </p>
                          {doc?.files?.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">{doc?.files?.length} file{doc?.files?.length > 1 ? 's' : ''} uploaded</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-border space-y-3">
                  <Button variant="default" fullWidth iconName="Save" iconPosition="left" onClick={handleSubmit} disabled={!isFormValid() || submitting}>
                    {submitting ? 'Submitting...' : 'Complete Onboarding'}
                  </Button>
                  <Button variant="outline" fullWidth iconName="Mail" iconPosition="left" onClick={generateEmailReminder}>
                    Send Document Reminder
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TenantOnboardingManagementScreen;